import { z } from "zod";

/**
 * My Tickets for a purchase bought on a seller's site, as the server
 * compiles it (`collectFor` in `@libs/buy-on-behalf`): every sentence,
 * action and code the client draws, so the client holds no seller
 * knowledge. Sent on the tickets reads (the api's `accesses.viewTicket`,
 * Platform API's tickets route) beside today's ticket fields.
 */

/** A link My Tickets opens. A seller's link stored for it (a venue app's
 *  page) is checked with this same schema where it is stored. */
export const collectUrlSchema = z.url();

/** Where "Open …" goes: the seller's (or its ticket app's) app, opened by
 *  its URL scheme where it has a verified one and otherwise by its store
 *  page, or a site. */
export const collectOpenSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("app"),
    label: z.string().min(1),
    scheme: z.string().min(1).nullable(),
    appStore: collectUrlSchema,
    playStore: collectUrlSchema,
  }),
  z.object({ kind: z.literal("site"), label: z.string().min(1), url: collectUrlSchema }),
]);
export type CollectOpen = z.infer<typeof collectOpenSchema>;

/** One of the seller's own released ticket codes, as the carousel draws
 *  it. Only `QR_CODE` (`isQr`) is drawn as a code. */
export const vendorTicketCodeSchema = z.object({
  key: z.string().min(1),
  codeType: z.string(),
  code: z.string(),
  isQr: z.boolean(),
  label: z.string(),
  /** "Ticket number ending 1234", when the seller gives the digits. */
  numberLine: z.string().nullable(),
});
export type VendorTicketCodeView = z.infer<typeof vendorTicketCodeSchema>;

export const collectNoticeSchema = z.object({
  key: z.string().min(1),
  /** `pending`: the seller releases them later; `processing`: being
   *  collected; `with_seller`: the seller keeps them (its account, app or
   *  email); `in_app`: the seller's ticket app holds them. */
  kind: z.enum(["pending", "processing", "with_seller", "in_app"]),
  title: z.string().min(1),
  body: z.string().min(1),
});
export type CollectNotice = z.infer<typeof collectNoticeSchema>;

export const collectSchema = z
  .object({
    notices: z.array(collectNoticeSchema),
    /** The seller's released codes: the owner's alone, [] for anyone else. */
    codes: z.array(vendorTicketCodeSchema),
    /** My Tickets' "?": how these tickets work and what was bought. */
    help: z.object({
      subtitle: z.string().min(1),
      /** "Your order": "2 × General Admission". */
      order: z.object({
        heading: z.string().min(1),
        lines: z.array(z.object({ key: z.string().min(1), text: z.string().min(1) })),
      }),
      /** The seller's references ("Skiddle booking reference"). */
      references: z.array(z.object({ label: z.string().min(1), value: z.string().min(1) })),
      /** The orders' ticket documents: the owner's alone, [] otherwise. */
      documents: z.array(
        z.object({ kind: z.enum(["pdf", "page"]), label: z.string().min(1), url: collectUrlSchema }),
      ),
      /** The account Lissen bought in, one login per order: the owner's
       *  alone, null otherwise. The login itself is never here; the owner
       *  reveals it through its own endpoint, a password or, for a seller
       *  that signs in by emailed code, the email only. */
      account: z
        .object({
          heading: z.string().min(1),
          body: z.string().min(1),
          reveal: z.enum(["password", "emailed_code"]),
          orders: z.array(z.object({ attemptId: z.string().min(1), label: z.string().min(1) })),
          open: collectOpenSchema.nullable(),
        })
        .nullable(),
      /** "Open …" for a purchase without an account box. */
      open: collectOpenSchema.nullable(),
    }),
    /** The listing (`vendor_events.id`) the purchase was bought from, which
     *  ticket analytics name instead of the seller; null for a purchase
     *  with no recorded listing. */
    listingId: z.string().min(1).nullable(),
  })
  .strict();
export type Collect = z.infer<typeof collectSchema>;

/** A tickets read's `collect`: null when the tickets are not a purchase
 *  bought on a seller's site (engine tickets, waitlist places), so there is
 *  no seller order to collect from. */
export const collectOrNullSchema = collectSchema.nullable();
